// Утилита для работы с push-уведомлениями

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

class PushNotifications {
  constructor(apiBaseUrl) {
    this.apiBaseUrl = apiBaseUrl;
    this.registration = null;
    this.subscription = null;
  }

  // Проверить поддержку push-уведомлений
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Зарегистрировать Service Worker
  async registerServiceWorker() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported');
    }

    try {
      this.registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('[Push] Service Worker registered');
      return this.registration;
    } catch (err) {
      console.error('[Push] Service Worker registration failed:', err);
      throw err;
    }
  }

  // Получить публичный VAPID ключ с сервера
  async getPublicKey() {
    const response = await fetch(`${this.apiBaseUrl}/push/public-key`);
    if (!response.ok) {
      throw new Error('Failed to get public key');
    }
    const { publicKey } = await response.json();
    return publicKey;
  }

  // Подписаться на push-уведомления
  async subscribe(token) {
    if (!this.registration) {
      await this.registerServiceWorker();
    }

    // Получить публичный VAPID ключ
    const publicKey = await this.getPublicKey();

    // Подписаться на push
    const subscription = await this.registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    console.log('[Push] Subscribed:', subscription);

    // Отправить подписку на сервер
    const response = await fetch(`${this.apiBaseUrl}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ subscription })
    });

    if (!response.ok) {
      throw new Error('Failed to subscribe on server');
    }

    this.subscription = subscription;
    console.log('[Push] Subscription sent to server');
    return subscription;
  }

  // Отписаться от push-уведомлений
  async unsubscribe(token) {
    if (!this.registration) {
      return;
    }

    const subscription = await this.registration.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    // Отписаться локально
    await subscription.unsubscribe();

    // Удалить подписку на сервере
    await fetch(`${this.apiBaseUrl}/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ endpoint: subscription.endpoint })
    });

    this.subscription = null;
    console.log('[Push] Unsubscribed');
  }

  // Проверить, есть ли активная подписка
  async hasSubscription() {
    if (!this.registration) {
      try {
        this.registration = await navigator.serviceWorker.getRegistration();
      } catch (err) {
        return false;
      }
    }

    if (!this.registration) {
      return false;
    }

    const subscription = await this.registration.pushManager.getSubscription();
    return subscription !== null;
  }

  // Запросить разрешение на уведомления
  async requestPermission() {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  // Проверить статус разрешения
  getPermission() {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }
}

export default PushNotifications;
